/**
 * Get the localized name for a service, category, or package.
 * Supports the new `translations` JSON field and the legacy nameEnglish/nameEn/nameAr/nameHe fields.
 * Falls back to the main `name` field if no translation exists for the requested language.
 *
 * @param {Object} entity
 * @param {string} language - e.g. 'en', 'ar', 'he'
 * @returns {string}
 */
export function getDisplayNameForLocale(entity, language) {
  if (!entity || typeof entity !== 'object') return '';
  const raw = (language || 'en').toLowerCase().split('-')[0];

  // 1. New translations JSON field
  if (entity.translations && typeof entity.translations === 'object') {
    const t = entity.translations[raw];
    if (t && typeof t.name === 'string' && t.name.trim()) return t.name.trim();
  }

  // 2. Legacy flat fields
  if (raw === 'ar') {
    const ar = (entity.nameAr || '').trim();
    if (ar) return ar;
  }
  if (raw === 'he') {
    const he = (entity.nameHe || '').trim();
    if (he) return he;
  }
  // English / fallback
  return (entity.nameEn || entity.nameEnglish || entity.name || '').trim() || entity.id || '';
}

/**
 * Get the localized description for an entity.
 * @param {Object} entity
 * @param {string} language
 * @returns {string}
 */
export function getDescriptionForLocale(entity, language) {
  if (!entity || typeof entity !== 'object') return '';
  const raw = (language || 'en').toLowerCase().split('-')[0];

  // New translations JSON field
  if (entity.translations && typeof entity.translations === 'object') {
    const t = entity.translations[raw];
    if (t && typeof t.description === 'string' && t.description.trim()) return t.description.trim();
  }

  // Legacy flat fields
  if (raw === 'ar') {
    const ar = (entity.descriptionAr || '').trim();
    if (ar) return ar;
  }
  if (raw === 'he') {
    const he = (entity.descriptionHe || '').trim();
    if (he) return he;
  }
  return (entity.descriptionEn || entity.description || '').trim();
}

/**
 * A specialist's name in the shopper's language: `translations[lang].name` (the server sends it, transliterated),
 * else the name the owner typed. Unlike a service, a nameless specialist has no name — never an id.
 */
export function getSpecialistNameForLocale(specialist, language) {
  if (!specialist || typeof specialist !== 'object') return '';
  const raw = (language || 'en').toLowerCase().split('-')[0];
  const tr = specialist.translations;
  if (tr && typeof tr === 'object') {
    const t = tr[raw];
    if (t && typeof t.name === 'string' && t.name.trim()) return t.name.trim();
  }
  const typed = specialist.displayName ?? specialist.name;
  return typeof typed === 'string' ? typed.trim() : '';
}

/**
 * The name for a leg of a booking (a slot or a phase that carries `specialistId` and a `specialistName` string): the
 * roster's person, in the page language, else the name the leg already carries.
 */
export function localizedPhaseSpecialistName(leg, roster, language) {
  const carried = typeof leg?.specialistName === 'string' ? leg.specialistName.trim() : '';
  const id = leg?.specialistId ?? leg?.specialist?._id ?? leg?.specialist?.id;
  if (id == null || !Array.isArray(roster)) return carried;
  const person = roster.find((p) => String(p?._id ?? p?.id) === String(id));
  return getSpecialistNameForLocale(person, language) || carried;
}
